import * as z from 'zod'
import { MediaSchema } from './Media'

const RoleSchema = z.enum(['user', 'admin'])
const PasswordSchema = z.string().min(8)

export const UserSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    image: z.string().nullable(),
    media: MediaSchema.optional().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    banned: z.boolean().nullable(),
    banReason: z.string().nullable(),
    banExpires: z.coerce.date().nullable(),
    isAnonymous: z.boolean().nullable(),
    role: RoleSchema,
})

export type User = z.infer<typeof UserSchema>

/**
 * Trimmed user shape for org-scoped lookups (avatars, chat logs, member
 * lists). Served by GET /:audience/users/:id, which only resolves users
 * visible to the caller's active organization.
 */
export const UserPreviewSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    image: z.string().nullable(),
    isAnonymous: z.boolean().nullable(),
    banned: z.boolean().nullable(),
})

export type UserPreview = z.infer<typeof UserPreviewSchema>

export const PasswordChangeSchema = z
    .object({
        password: PasswordSchema,
        passwordConfirmation: PasswordSchema,
    })
    .refine((data) => data.password === data.passwordConfirmation, {
        params: {
            i18n: 'errors.passwordsDoNotMatch',
        },
        path: ['passwordConfirmation'],
    })

export type PasswordChange = z.infer<typeof PasswordChangeSchema>

export const PasswordChangeWithCurrentSchema = PasswordChangeSchema.extend({
    currentPassword: PasswordSchema,
})

export type PasswordChangeWithCurrent = z.infer<
    typeof PasswordChangeWithCurrentSchema
>

export const UserCreateSchema = PasswordChangeSchema.extend({
    name: z.string().min(1),
    email: z.email(),
    media: MediaSchema.optional(),
    role: RoleSchema.optional().default('user'),
})

export type UserCreate = z.infer<typeof UserCreateSchema>

export const UserUpdateSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    emailVerified: z.boolean().optional(),
    image: z.string().nullish(),
    role: RoleSchema,
    media: MediaSchema.optional().nullable(),
    banned: z.boolean().nullish(),
    banReason: z.string().nullish(),
    banExpires: z.string().nullish(),
    isAnonymous: z.boolean().nullish(),
})

export type UserUpdate = z.infer<typeof UserUpdateSchema>

export const SignUpSchema = PasswordChangeSchema.extend({
    name: z.string().min(1),
    email: z.email(),
    rememberMe: z.boolean().optional(),
})

export type SignUp = z.infer<typeof SignUpSchema>
